// Generates a downloadable protocol report (Markdown or Word) from the
// /api/protocols/:id/summary response. Used by SummaryView and the Guided
// Conversation completion screen; reused by Agent Chat as its export step.
//
// The report includes only COMPLETED sections (the narrative content the user
// produced) followed by a short list of items to finish directly in InfoEd.
// All generation is client-side, reusing the already-formatted summary data so
// the report matches exactly what the user sees in the Summary view.
//
// Word output is Word-compatible HTML saved as .doc — opens and edits cleanly
// in Word / Google Docs with no external library (the `docx` npm package does
// not build under Create React App's Babel config).

export interface ReportQuestion {
  number: string;
  text: string;
  type: string;
  answer: string;
  answered: boolean;
}
export interface ReportSection {
  number: number;
  name: string;
  questions: ReportQuestion[];
}
export interface ReportData {
  protocolId: number;
  sections: ReportSection[];
}

export type ReportFormat = 'word' | 'markdown';

// Strip common markdown so output is clean plain text (answers may contain
// **bold**, headers, etc. from the summary formatter).
const stripMarkdown = (text: string): string =>
  (text || '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^---+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const escapeHtml = (text: string): string =>
  (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const isAnswered = (q: ReportQuestion): boolean =>
  q.answered && !!q.answer && q.answer.trim() !== '' && q.answer.trim() !== '[Not answered]';

const safeFilename = (title: string): string =>
  (title || 'IACUC_protocol').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'IACUC_protocol';

const generatedNote = (): string => {
  const date = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  return `IACUC protocol draft — generated ${date}. Review and edit before entering into InfoEd.`;
};

// Returns { completed: sections containing answered questions, outstanding: unanswered questions }
const partition = (data: ReportData) => {
  const completed = data.sections
    .map(s => ({ ...s, questions: s.questions.filter(isAnswered) }))
    .filter(s => s.questions.length > 0);
  const outstanding: ReportQuestion[] = [];
  data.sections.forEach(s => s.questions.forEach(q => { if (!isAnswered(q)) outstanding.push(q); }));
  return { completed, outstanding };
};

export function generateMarkdown(data: ReportData, title: string): string {
  const { completed, outstanding } = partition(data);
  const lines: string[] = [`# ${title || 'IACUC Protocol'}`, '', `_${generatedNote()}_`, ''];

  completed.forEach(section => {
    lines.push(`## Section ${section.number}: ${section.name}`, '');
    section.questions.forEach(q => {
      lines.push(`### ${q.number} — ${q.text}`, '', q.answer.trim(), '');
    });
  });

  if (outstanding.length > 0) {
    lines.push('## Still to complete directly in InfoEd', '');
    outstanding.forEach(q => lines.push(`- ${q.number} — ${q.text}`));
    lines.push('');
  }

  return lines.join('\n');
}

// Word-compatible HTML document (saved as .doc).
export function generateWordHtml(data: ReportData, title: string): string {
  const { completed, outstanding } = partition(data);
  const body: string[] = [
    `<h1>${escapeHtml(title || 'IACUC Protocol')}</h1>`,
    `<p style="color:#555"><i>${escapeHtml(generatedNote())}</i></p>`,
  ];

  completed.forEach(section => {
    body.push(`<h1>Section ${section.number}: ${escapeHtml(section.name)}</h1>`);
    section.questions.forEach(q => {
      body.push(`<h2>${escapeHtml(q.number)} — ${escapeHtml(q.text)}</h2>`);
      stripMarkdown(q.answer)
        .split('\n')
        .forEach(line => body.push(line.trim() ? `<p>${escapeHtml(line.trim())}</p>` : '<p>&nbsp;</p>'));
    });
  });

  if (outstanding.length > 0) {
    body.push('<h1>Still to complete directly in InfoEd</h1>');
    body.push('<ul>');
    outstanding.forEach(q => body.push(`<li>${escapeHtml(q.number)} — ${escapeHtml(q.text)}</li>`));
    body.push('</ul>');
  }

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${escapeHtml(title || 'IACUC Protocol')}</title>
<style>body{font-family:Calibri,Arial,sans-serif;font-size:11pt;line-height:1.4;} h1{font-size:16pt;color:#3E1C84;} h2{font-size:13pt;color:#3E1C84;}</style>
</head>
<body>${body.join('\n')}</body></html>`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Convenience: build the chosen format and trigger a browser download.
export function downloadReport(data: ReportData, title: string, format: ReportFormat): void {
  const base = safeFilename(title) + '_IACUC_draft';
  if (format === 'markdown') {
    const blob = new Blob([generateMarkdown(data, title)], { type: 'text/markdown;charset=utf-8' });
    triggerDownload(blob, base + '.md');
  } else {
    const blob = new Blob([generateWordHtml(data, title)], { type: 'application/msword' });
    triggerDownload(blob, base + '.doc');
  }
}
